import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Scale } from "lucide-react";
import {
  Button,
  DetailSection,
  EmptyState,
  ListCard,
  SkeletonList,
  SkeletonText,
  StatusBadge,
  Textarea,
  Timestamp,
  useSubmittingMutation,
  useToast,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCalendarDate, formatDateInTz } from "@/design-system/lib/format";
import { PortalApiError } from "../api/client";
import {
  vendorAuditApi,
  type VendorAuditComment,
  type VendorAuditDetail,
  type VendorAuditDetermination,
  type VendorAuditQuote,
  type VendorAuditRecommendation,
  type VendorAuditStatus,
  type VendorAuditSummary,
} from "../api/vendorAudit";

// Annual Vendor Audit — the vendor's side of the yearly market check.
//
// This reads as a DOCUMENT, not a form. Once a year Phoenix records what it pays
// this vendor, what it gets for it, and what competitors quoted; forms a
// preliminary view; and releases the whole thing here for the vendor to respond
// to BEFORE any determination is made. So the surface is: the period, the two
// summaries, every competitor and what they said, Phoenix's view, and a real
// two-sided discussion — in that order, in prose.
//
// Nothing is redacted. Full competitor names, full quoted terms. A market check
// a vendor cannot see the evidence for is not one they can meaningfully respond
// to, and the response is the entire reason it is released.
//
// No React Query in the portal: the list loads with useState and the reply
// mutation re-runs the list loader through `useSubmittingMutation`'s `refresh`
// (the parent still holds the list state while the document is open).

const STATUS_TONE: Record<VendorAuditStatus, StatusTone> = {
  "in-vendor-review": "warn",
  "awaiting-final-assessment": "progress",
  completed: "neutral",
};

const STATUS_LABEL: Record<VendorAuditStatus, string> = {
  // The vendor's own framing of each state — what it means for THEM, not the
  // internal lifecycle name.
  "in-vendor-review": "Open for your response",
  "awaiting-final-assessment": "With Phoenix for final assessment",
  completed: "Completed",
};

const DETERMINATION_LABEL: Record<VendorAuditDetermination, string> = {
  competitive: "Market-competitive",
  "not-competitive": "Not market-competitive",
  inconclusive: "Inconclusive",
};

// A completed audit's overall pill stays neutral; the DETERMINATION carries the
// verdict's own tone, so a "not competitive" year never reads as a green tick.
const DETERMINATION_TONE: Record<VendorAuditDetermination, StatusTone> = {
  competitive: "ok",
  "not-competitive": "warn",
  inconclusive: "neutral",
};

const RECOMMENDATION_LABEL: Record<VendorAuditRecommendation, string> = {
  retain: "Retain",
  renegotiate: "Renegotiate terms",
  replace: "Replace",
};

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

function auditTitle(year: number): string {
  return `${year} annual market check`;
}

/** "12 days left" / "1 day left" / "Closes today". */
function daysLeftPhrase(days: number): string {
  if (days <= 0) return "Closes today";
  return `${days} day${days === 1 ? "" : "s"} left`;
}

export function VendorAuditTab() {
  const [rows, setRows] = useState<VendorAuditSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const openAudit = params.get("audit");

  const loadList = useCallback(async () => {
    const data = await vendorAuditApi.list();
    setRows(data);
    return data;
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await vendorAuditApi.list();
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load your market checks."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setOpenAudit = (id: string | null) => {
    const updated = new URLSearchParams(params);
    if (id) updated.set("audit", id);
    else updated.delete("audit");
    setParams(updated, { replace: true });
  };

  if (openAudit) {
    return (
      <AuditDocument
        auditId={openAudit}
        onBack={() => setOpenAudit(null)}
        refreshList={loadList}
      />
    );
  }

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }

  if (!rows) {
    return <SkeletonList rows={3} data-testid="vendor-audits-loading" />;
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Scale}
        title="No market check yet"
        description="Once a year we check that what we pay you is still in line with the market — what we pay, what we get for it, and what other providers quote for the same work. When we've done this year's, it will appear here for you to read and respond to before we finish it."
      />
    );
  }

  return (
    <section aria-label="Annual market checks" className="flex flex-col gap-space-3">
      {rows.map((row) => (
        <ListCard
          key={row.id}
          data-testid={`vendor-audit-row-${row.id}`}
          title={auditTitle(row.auditYear)}
          status={
            <StatusBadge tone={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</StatusBadge>
          }
          meta={
            <span className="flex flex-wrap items-center gap-x-space-2 gap-y-space-1">
              {row.isOpenForComment && row.reviewClosesAt ? (
                <span data-testid={`vendor-audit-due-${row.id}`}>
                  Respond by {formatDateInTz(row.reviewClosesAt)}
                  {row.daysRemaining !== null && <> · {daysLeftPhrase(row.daysRemaining)}</>}
                </span>
              ) : row.finalDetermination ? (
                <span>
                  {DETERMINATION_LABEL[row.finalDetermination]}
                  {row.finalRecommendation && (
                    <> · {RECOMMENDATION_LABEL[row.finalRecommendation]}</>
                  )}
                </span>
              ) : (
                <span>Closed for comment — we're finishing our assessment</span>
              )}
              {row.commentCount > 0 && (
                <>
                  <span aria-hidden>·</span>
                  <span>
                    {row.commentCount} message{row.commentCount === 1 ? "" : "s"}
                  </span>
                </>
              )}
            </span>
          }
          actions={
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setOpenAudit(row.id)}
              data-testid={`vendor-audit-open-${row.id}`}
            >
              {row.isOpenForComment ? "Read & respond" : "View"}
            </Button>
          }
        />
      ))}
    </section>
  );
}

function AuditDocument({
  auditId,
  onBack,
  refreshList,
}: {
  auditId: string;
  onBack: () => void;
  refreshList: () => Promise<unknown>;
}) {
  const toast = useToast();
  const [audit, setAudit] = useState<VendorAuditDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await vendorAuditApi.get(auditId);
        if (!cancelled) setAudit(data);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load this market check."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auditId]);

  // The response DTO covers this component's own subtree; `refresh` re-runs the
  // loader the PARENT owns, so the history row's message count doesn't go stale
  // behind us.
  const sendReply = useSubmittingMutation(() => vendorAuditApi.comment(auditId, reply), {
    refresh: refreshList,
    onSuccess: (updated) => {
      setAudit(updated);
      setReply("");
      toast.success("Sent to Phoenix.");
    },
    onError: (err) => toast.error(errorMessage(err, "Couldn't send your response. Try again.")),
  });

  const backButton = (
    <Button type="button" variant="text" size="sm" onClick={onBack} data-testid="vendor-audit-back">
      <ArrowLeft size={16} aria-hidden />
      Back to market checks
    </Button>
  );

  if (error) {
    return (
      <div className="flex flex-col gap-space-3">
        {backButton}
        <p className="text-small text-status-error" role="alert">
          {error}
        </p>
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="flex flex-col gap-space-3">
        {backButton}
        <DetailSection>
          <SkeletonText lines={8} />
        </DetailSection>
      </div>
    );
  }

  return (
    <article
      aria-label={auditTitle(audit.auditYear)}
      className="flex flex-col gap-space-4"
    >
      <div className="flex items-center justify-between gap-space-3">
        {backButton}
        <StatusBadge tone={STATUS_TONE[audit.status]}>{STATUS_LABEL[audit.status]}</StatusBadge>
      </div>

      <header>
        <h2 className="m-0 font-display text-h3 font-bold text-onyx">
          {auditTitle(audit.auditYear)}
        </h2>
        {audit.releasedAt && (
          <p className="mt-space-1 text-small text-gray-mid">
            Shared with you on {formatDateInTz(audit.releasedAt)}
          </p>
        )}
      </header>

      <DeadlineNotice audit={audit} />

      <DetailSection title="What we pay you">
        <Prose value={audit.phoenixPricingSummary} testId="vendor-audit-pricing" />
      </DetailSection>

      <DetailSection title="What we receive for it">
        <Prose value={audit.phoenixServicesSummary} testId="vendor-audit-services" />
      </DetailSection>

      <DetailSection
        title="Who else we asked"
        accessory={
          audit.quotes.length > 0 ? (
            <span className="text-caption text-gray-mid">
              {audit.quotes.length} approached
            </span>
          ) : undefined
        }
      >
        {audit.quotes.length === 0 ? (
          <p className="m-0 text-small text-gray-mid" data-testid="vendor-audit-no-quotes">
            No competitor quotes were recorded for this market check.
          </p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-space-3 p-0">
            {audit.quotes.map((quote) => (
              <CompetitorEntry key={quote.id} quote={quote} />
            ))}
          </ul>
        )}
      </DetailSection>

      <DetailSection title="Our preliminary view">
        <Prose value={audit.preliminaryFinding} testId="vendor-audit-finding" />
      </DetailSection>

      {audit.finalDetermination && (
        <DetailSection
          title="Our determination"
          accessory={
            <StatusBadge tone={DETERMINATION_TONE[audit.finalDetermination]}>
              {DETERMINATION_LABEL[audit.finalDetermination]}
            </StatusBadge>
          }
        >
          <div className="flex flex-col gap-space-2" data-testid="vendor-audit-determination">
            {audit.finalRecommendation && (
              <p className="m-0 text-small text-onyx">
                <span className="font-semibold">What we plan to do: </span>
                {RECOMMENDATION_LABEL[audit.finalRecommendation]}
              </p>
            )}
            <Prose value={audit.finalNarrative} />
            {audit.completedAt && (
              <p className="m-0 text-caption text-gray-mid">
                Recorded {formatDateInTz(audit.completedAt)}
              </p>
            )}
          </div>
        </DetailSection>
      )}

      <Discussion
        audit={audit}
        reply={reply}
        onReplyChange={setReply}
        onSend={() => void sendReply.submit()}
        sending={sendReply.pending}
      />
    </article>
  );
}

/**
 * The deadline, said plainly: when it closes, how long is left, and — the part
 * that actually matters — what happens if they say nothing. A window whose
 * consequence is unstated is a window most people ignore.
 */
function DeadlineNotice({ audit }: { audit: VendorAuditDetail }) {
  if (audit.isOpenForComment) {
    return (
      <div
        role="note"
        data-testid="vendor-audit-deadline"
        className="rounded-md border border-status-warning-ink bg-status-warning/10 p-space-3 text-small text-onyx"
      >
        <p className="m-0 font-semibold">
          {audit.reviewClosesAt
            ? `Your response is due by ${formatDateInTz(audit.reviewClosesAt)}`
            : "Your response is due"}
          {audit.daysRemaining !== null && <> — {daysLeftPhrase(audit.daysRemaining)}.</>}
        </p>
        <p className="m-0 mt-space-1">
          Tell us anything we've got wrong or left out — pricing, what's included,
          or a competitor we should have compared you against. If we don't hear
          from you by then, this market check moves to final assessment without
          your response.
        </p>
      </div>
    );
  }

  if (audit.status === "awaiting-final-assessment") {
    return (
      <div
        role="note"
        data-testid="vendor-audit-deadline"
        className="rounded-md border border-border-light bg-white-warm p-space-3 text-small text-onyx"
      >
        The response window
        {audit.reviewClosedAt ? ` closed on ${formatDateInTz(audit.reviewClosedAt)}` : " has closed"}
        . We're completing our assessment now and will record our determination
        here.
      </div>
    );
  }

  return (
    <div
      role="note"
      data-testid="vendor-audit-deadline"
      className="rounded-md border border-border-light bg-white-warm p-space-3 text-small text-onyx"
    >
      This market check is closed. It stays here in full — including anything you
      told us and how we replied — as the record of what we concluded and why.
    </div>
  );
}

/**
 * One competitor. A DECLINE is a result, not a blank: it is the evidence behind
 * an inconclusive determination, so it reads as "we asked, they wouldn't quote"
 * rather than as an empty row the vendor reads as a data problem.
 */
function CompetitorEntry({ quote }: { quote: VendorAuditQuote }) {
  const contactBits = [
    quote.contactName,
    quote.contactMethod ? `by ${quote.contactMethod.toLowerCase()}` : null,
    // A wall-calendar date (the day they were called), not an instant — the UTC
    // formatter, never the timezone-aware one.
    quote.contactedOn ? `on ${formatCalendarDate(quote.contactedOn)}` : null,
  ].filter(Boolean);

  return (
    <li
      className="rounded-md border border-border-light bg-white-warm p-space-3"
      data-testid={`vendor-audit-quote-${quote.id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-space-2">
        <span className="text-small font-semibold text-onyx">{quote.competitorName}</span>
        <StatusBadge tone={quote.declined ? "neutral" : "info"}>
          {quote.declined ? "Would not quote" : "Quoted"}
        </StatusBadge>
      </div>

      {contactBits.length > 0 && (
        <p className="m-0 mt-space-1 text-caption text-gray-mid">
          Contacted {contactBits.join(" ")}
        </p>
      )}

      {quote.declined ? (
        <p className="m-0 mt-space-2 text-small text-gray-mid">
          We approached them for a comparable quote and they declined to give one.
        </p>
      ) : (
        <dl className="m-0 mt-space-2 flex flex-col gap-space-2 text-small">
          <QuoteLine label="They quoted" value={quote.quotedTerms} />
          <QuoteLine label="That includes" value={quote.servicesOffered} />
        </dl>
      )}
    </li>
  );
}

function QuoteLine({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-caption uppercase tracking-label text-gray-mid">{label}</dt>
      <dd className="m-0 whitespace-pre-wrap text-onyx">
        {value?.trim() ? value : <span className="text-gray-mid">Not recorded</span>}
      </dd>
    </div>
  );
}

/**
 * The two-sided discussion: append-only, attributed, timestamped, and open in
 * both directions while the window is. It closes with the audit but stays
 * readable forever — a determination without the conversation behind it is a
 * worse record than either.
 */
function Discussion({
  audit,
  reply,
  onReplyChange,
  onSend,
  sending,
}: {
  audit: VendorAuditDetail;
  reply: string;
  onReplyChange: (next: string) => void;
  onSend: () => void;
  sending: boolean;
}) {
  return (
    <DetailSection title="Discussion">
      {audit.comments.length === 0 ? (
        <p className="m-0 text-small text-gray-mid" data-testid="vendor-audit-thread-empty">
          {audit.isOpenForComment
            ? "Nothing here yet. Anything you send goes straight to the vendor manager running this check, and their reply appears in the same place."
            : "Nothing was raised on this market check."}
        </p>
      ) : (
        <ul
          className="m-0 flex list-none flex-col gap-space-3 p-0"
          data-testid="vendor-audit-thread"
        >
          {audit.comments.map((comment) => (
            <ThreadEntry key={comment.id} comment={comment} />
          ))}
        </ul>
      )}

      {audit.isOpenForComment ? (
        <div className="mt-space-4 flex flex-col gap-space-2">
          <label className="flex flex-col gap-space-1">
            <span className="text-caption uppercase tracking-label text-gray-mid">
              Your response
            </span>
            <Textarea
              value={reply}
              onChange={(e) => onReplyChange(e.target.value)}
              rows={4}
              disabled={sending}
              placeholder="Anything we've got wrong, left out, or should weigh differently."
              aria-label="Your response"
              data-testid="vendor-audit-reply"
            />
          </label>
          <div>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={onSend}
              disabled={sending || reply.trim().length === 0}
              loading={sending}
              data-testid="vendor-audit-reply-send"
            >
              Send to Phoenix
            </Button>
          </div>
        </div>
      ) : (
        <p
          className="mt-space-3 m-0 text-caption text-gray-mid"
          data-testid="vendor-audit-thread-closed"
        >
          This discussion is closed. It stays here as part of the record.
        </p>
      )}
    </DetailSection>
  );
}

function ThreadEntry({ comment }: { comment: VendorAuditComment }) {
  const fromPhoenix = comment.authorRole === "phoenix";
  return (
    <li
      data-testid={`vendor-audit-comment-${comment.id}`}
      className={
        fromPhoenix
          ? "rounded-md border border-gold border-l-4 bg-white-warm p-space-3"
          : "rounded-md border border-border-light border-l-4 bg-white-pure p-space-3"
      }
    >
      <p className="m-0 text-caption uppercase tracking-label text-gray-mid">
        {comment.authorName}
        {" · "}
        <Timestamp value={comment.createdAt} />
      </p>
      <p className="m-0 mt-space-1 whitespace-pre-wrap text-small text-onyx">{comment.body}</p>
    </li>
  );
}

/** Free-text the vendor manager typed. Blank means it was left blank — say so
 *  rather than rendering an empty gap the vendor reads as a loading failure. */
function Prose({ value, testId }: { value: string | null; testId?: string }) {
  if (!value?.trim()) {
    return (
      <p className="m-0 text-small text-gray-mid" data-testid={testId}>
        Not recorded.
      </p>
    );
  }
  return (
    <p className="m-0 whitespace-pre-wrap text-small text-onyx" data-testid={testId}>
      {value}
    </p>
  );
}
